
import { computed, defineComponent, onMounted, PropType, ref, watch } from 'vue'
import { CtCatalogue, CtCatalogueProduct, CtProduct } from '@/models/interfaces/content-type'
import { useStore } from 'vuex'
import EditableBlock from '@/components/EditableBlock.vue'
import EditableBlockInput from '@/components/EditableBlockInput.vue'
import { useQuasar } from 'quasar'
import { SYNCABLE_SERVICES } from '@/pages/Manage/Catalogues/constants'
import MultiSwitch from '@/components/MultiSwitch.vue'
import ChannelServiceLink from './ChannelServiceLink.vue'
import EventConfigEditor from '@/pages/Manage/Catalogues/components/EventConfigEditor.vue'

import DButton from '@/components/base/DButton.vue'
import AddIcon from '@/components/Icons/AddIcon.vue'


export default defineComponent({
  name: 'SalesChannelSettings',
  components: { MultiSwitch, EditableBlockInput, EditableBlock, ChannelServiceLink, EventConfigEditor, DButton, AddIcon },
  props: {
    catalogue: {
      type: Object as PropType<CtCatalogue>,
      required: true
    },
    readonly: {
      type: Boolean
    }
  },
  emits: ['update:catalogue'],
  setup (props, { emit }) {
    const $q = useQuasar()
    const $store = useStore()
    const $sys = $store.getters['Services/Sys']

    const services = ref<{ id: string, name: string, type: string, active: boolean, account?: { value: string, label: string }, [key: string]: any }[]>([])
    const showServiceModal = ref(false)
    const showServiceConfig = ref(false)
    const serviceOptions = computed(() => {
      return services.value
        .filter(service => SYNCABLE_SERVICES.includes(service.type))
        .map(({ name, id }) => ({ label: name, value: id }))
    })
    onMounted(async () => {
      services.value = (await $sys.get('/service'))
    })

    function configureEvents (editState) {
      showServiceConfig.value = true
    }

    const updateService = (editState, serviceId) => {
      if (editState.service) {
        editState.service.id = serviceId
      } else {
        editState.service = {
          id: serviceId,
          sync: {
            inventory: { active: 0 },
            order: { active: 0 },
            product: { active: 0 },
          }
        }
      }
    }

    const syncSwitchOptions = [
      { label: 'off', value: 0 },
      { label: 'pull', value: 1 },
      { label: 'push', value: 2 },
      { label: 'both', value: 3 },
    ]

    const setSync = (editState, editingRef, connectable, value) => {
      if (editState.service?.id) {
        if (!editingRef.value) (editingRef.value = true)
        editState.service.sync[connectable] = { ...(editState.service.sync[connectable] || {}), active: value }
      }
    }

    // watch(() => diffState.rhs, () => emit('diff', !!currentDiff.value))
    const onSyncSwitchesClick = (editState, editingRef) => {
      if (!editState.service?.id) {
        if (!editingRef.value) editingRef.value = true
        $q.notify({ message: 'Link to an external app first', type: 'negative', classes: 'custom-notify error' })
      }
    }

    function createNewService () {
      showServiceModal.value = true
    }

    async function handleServiceUnlink (editState) {
      if (!editState.service?.id) {
        editState.service = null
        showServiceModal.value = true
        return
      }
      const deleted = await $sys.services.delete(editState.service.id)
        .then(() => {
          editState.service = null
          showServiceModal.value = true
          emit('update:catalogue', props.catalogue?.sys?.id, { service: null })
        }).catch(e => {
          console.error(e)
          $q.notify({ message: 'Error removing link', caption: 'Save and refresh try again...', position: 'bottom', type: 'negative', classes: 'error custom-notify' })
          editState.service = null
        })

    }

    function getServiceType (serviceId: string) {
      return services.value?.find(s => s.id === serviceId)?.type || serviceId
    }

    function getServiceAccount (serviceId: string) {
      return services.value?.find(s => s.id === serviceId)?.account?.label || ''
    }

    return {
      services,
      serviceOptions,
      getServiceType,
      getServiceAccount,
      updateService,
      syncSwitchOptions,
      createNewService,
      onSyncSwitchesClick,
      setSync,
      showServiceModal,
      showServiceConfig,
      configureEvents,
      emit,
      handleServiceUnlink,
      clipped: () => {
        $q.notify({ message: 'Copied to clipboard', position: 'top', classes: 'custom-notify' })
      },
    }
  },

})
